/* eslint-disable max-lines */
import ApiService from './';
import { AssignFileParams, CreateFileParams, CreateWordDocParams, FetchFolderFileParams } from '../params/redux/file';
/**
 * @heading1: File API service.
 * @paragraph: This module provides a set of methods to interact with the File API.
 * It handles operations like Fetching file details and create files etc.
 */
const FileApi = {
    /**
     * @heading3: Fetch Files
     * @paragraph: Sends a POST request to retrieve all files available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The parameters required id on which the files will be fetch.
     * @returns: {Promise<unknown[]>} - The response containing a list of all files.
     * @separator:
     */
    fetchFiles: (token: string, client_id: number) => {
        // Perform a POST request to fetch files with the provided data
        return ApiService.apiHit({
            url: 'get-files',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: Create File
     * @paragraph: Sends a POST request to Create a File.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {CreateFileParams} data - The data containing the details for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    createFile: (token: string, data: CreateFileParams) => {
        // Perform a Post request to create file endpoint with the token
        return ApiService.apiHit({
            url: 'create-files',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Fetch Folder Files
     * @paragraph: Sends a POST request to fetch folder files.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {CreateFileParams} data - The data containing the details for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    fetchFolderFiles: (token: string, data: FetchFolderFileParams) => {
        // Perform a Post request to fetch folder files endpoint with the token
        return ApiService.apiHit({
            url: 'get-folder-files',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
         * @heading3: Create document Files
         * @paragraph: Sends a POST request to create document file.
         * @param: {string} token - The user's authentication token, required for authorization.
         * @param: {CreateWordDocParams} data - The data containing the details for the operation.
         * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
         * @separator:
         */
    createDocumentFile: (token: string, data: CreateWordDocParams) => {
        // Perform a Post request to create document file endpoint with the token
        return ApiService.apiHit({
            url: 'create-word-document',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
         * @heading3: delete File
         * @paragraph: Sends a POST request to delete file.
         * @param: {string} token - The user's authentication token, required for authorization.
         * @param: {number} client_id - The client_id for the operation.
         * @param: {number} file_id - The file_id for the operation.
         * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
         * @separator:
         */
    deleteFile: (token: string, client_id: number, file_id: number) => {
        // Perform a Post request to delete file endpoint with the token
        return ApiService.apiHit({
            url: 'delete-file',
            method: 'post',
            token,
            data: { client_id, file_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
         * @heading3: fetch recent Files
         * @paragraph: Sends a POST request to fetch recent files.
         * @param: {string} token - The user's authentication token, required for authorization.
         * @param: {number} client_id - The client_id for the operation.
         * @param: {number} file_id - The file_id for the operation.
         * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
         * @separator:
         */
    fetchRecentFiles: (token: string, client_id: number) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'get-recent-files',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    assignFile: (token: string, data: AssignFileParams) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'assign-file',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchAssignFiles: (token: string) => {
        return ApiService.apiHit({
            url: 'get-assign-file',
            method: 'post',
            token,
            data: {},
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    removeAssingFile: (token: string, data: AssignFileParams) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'remove-assign-file',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    getWordDocument: (token: string, file_id: number) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'get-word-document',
            method: 'post',
            token,
            data: { file_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    editWordDocument: (token: string, html: string, file_name: string, file_id: number) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'edit-word-document',
            method: 'post',
            token,
            data: { file_id, html, file_name },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    assignTaskToFile: (token: string, task_id: number, client_id: number, file_id: number) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'assign-task-to-file',
            method: 'post',
            token,
            data: { task_id, client_id, file_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchSingleFileDetails: (token: string, file_id: number) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'get-single-file-edit',
            method: 'post',
            token,
            data: { file_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    editFileName: (token: string, file_id: number, file_name: string, client_id: number) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'single-file-edit',
            method: 'post',
            token,
            data: { file_id, file_name, client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    addFileComment: (token: string, file_id: number, comment: string) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'add-file-comment',
            method: 'post',
            token,
            data: { file_id, comment },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    deleteFileComment: (token: string, comment_id: number) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'delete-file-comment',
            method: 'post',
            token,
            data: { comment_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    deleteFileTask: (token: string, task_id: number, file_id: number, client_id: number) => {
        // Perform a Post request to fetch recent files endpoint with the token
        return ApiService.apiHit({
            url: 'remove-task-to-file',
            method: 'post',
            token,
            data: { task_id, file_id, client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchDocFile: (token: string, file_id: number) => {
        return ApiService.apiHit({
            url: 'get-word-document',
            method: 'post',
            token,
            data: { file_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    downloadFile: (token: string, path: string) => {
        return ApiService.apiHit({
            url: `download-file/${path}`,
            method: 'get',
            token
        });
    },
    dragFileToFolder: (token: string, file_id: number, folder_id: number) => {
        return ApiService.apiHit({
            url: 'move-file',
            method: 'post',
            token,
            data: { file_id, folder_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
                }
            });
    }
};
export default FileApi;
